<template>
  <v-card autofocus @keydown.esc="cancel">
    <v-toolbar :color="event.color" dense dark elevation="1">
      <v-app-bar-nav-icon>
        <v-icon>mdi-calendar</v-icon>
      </v-app-bar-nav-icon>
      <v-spacer />
      <v-toolbar-title>
        イベント
      </v-toolbar-title>
      <v-spacer />
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn icon @click="editEvent" v-on="on">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </template>
        <span>編集</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn icon @click="removeEvent" v-on="on">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
        <span>削除</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn icon @click="cancel" v-on="on">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
        <span>閉じる</span>
      </v-tooltip>
    </v-toolbar>
    <v-card-text>
      <v-skeleton-loader
        :loading="eventLoading"
        type="list-item-avatar"
        transition="fade-transition"
        ><v-row>
          <v-col cols="1"><v-icon medium>mdi-chart-bubble</v-icon></v-col>
          <v-col cols="11">
            <p class="filterheader-label text-caption pb-1">
              <span>プレイリスト名</span>
            </p>
            <h2 class="text-h3 font-weight-medium">
              {{ getPlaylistName(event.playlist) }}
            </h2>
          </v-col>
        </v-row>
      </v-skeleton-loader>
      <v-divider></v-divider>
      <v-skeleton-loader
        :loading="eventLoading"
        type="list-item-avatar"
        transition="fade-transition"
        ><v-row class="mt-2">
          <v-col cols="1"><v-icon medium>mdi-camera</v-icon></v-col>
          <v-col cols="11">
            <p class="filterheader-label text-caption pb-1">
              <span>出力先</span>
            </p>
            <v-chip
              class="ma-1"
              label
              v-for="struct in getStructureFromDevices(event.devices)"
              :key="struct.id"
            >
              <v-avatar left>
                <v-icon>{{ struct.icon }}</v-icon>
              </v-avatar>
              {{ struct.name }}
            </v-chip>
          </v-col>
        </v-row>
      </v-skeleton-loader>
      <v-divider></v-divider>
      <v-row class="mt-4">
        <v-col cols="1"><v-icon medium>mdi-calendar-clock</v-icon></v-col>
        <v-col cols="11">
          <p class="filterheader-label text-caption pb-1">
            <span>開始終了時間</span>
          </p>
          <h2 class="text-h4 font-weight-medium">
            {{ event.startDate }} ～ {{ event.endDate }}
          </h2>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="1"><v-icon medium>mdi-clock</v-icon></v-col>
        <v-col cols="5">
          <p class="filterheader-label text-caption pb-1">
            <span>範囲時間</span>
          </p>
          <h2 class="text-h4 font-weight-medium">
            {{ getTimeArea() }}
          </h2>
        </v-col>
        <v-col cols="5">
          <p class="filterheader-label text-caption pb-1">
            <span>繰り返し</span>
          </p>
          <h2 class="text-h4 font-weight-medium">
            {{ getPatternName(event.pattern) }}
          </h2>
        </v-col>
      </v-row>
    </v-card-text>
    <v-dialog
      v-model="editDialogOpen"
      max-width="720px"
      transition="slide-x-reverse-transition"
      class="event-dialog"
    >
      <schedule-event-card
        :event="eventDraft"
        editMode
        @save="saveEvent"
        @cancel="editDialogOpen = false"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { eventFunctionsMixin, deviceFunctionsMixin } from '@/plugins/mixins'
import ScheduleEventCard from '@/components/schedule/ScheduleEventCard'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import { concat, flatten } from 'lodash/core'
import { uniq } from 'lodash/array'

export default {
  name: 'ScheduleEventDraft',
  mixins: [eventFunctionsMixin, deviceFunctionsMixin],
  props: {
    event: {
      type: Object,
      required: true
    },
    removable: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: true
    }
  },
  components: {
    ScheduleEventCard
  },
  data() {
    return {
      datePickerOpen: false,
      eventLoading: true,
      editDialogOpen: false,
      patterns: this.getEventRepeatPatterns(),
      eventDraft: {}
    }
  },
  computed: {
    ...mapGetters({
      devices: 'devices/getDevices',
      units: 'devices/units/getUnits',
      groups: 'devices/groups/getGroups',
      playlists: 'playlists/getPlaylists'
    })
  },
  methods: {
    ...mapActions({
      bindPlaylistsRef: 'playlists/bindRef',
      bindDevicesRef: 'devices/bindRef',
      bindDeviceGroupsRef: 'devices/groups/bindRef',
      bindDeviceUnitsRef: 'devices/units/bindRef'
    }),
    cancel() {
      this.editDialogOpen = false
      this.$emit('cancel')
    },
    removeEvent() {
      this.$emit('remove', this.event)
    },
    getDeviceIcon(type) {
      return this.getDeviceType().find(d => d.type === type).icon
    },
    getPlaylistName(id) {
      const playlist = this.playlists.find(p => p.id === id)
      return playlist ? playlist.name : 'なし'
    },
    getPatternName(repeatType) {
      return this.getEventRepeatPatterns().find(r => r.value === repeatType)
        .text
    },
    getStructureFromDevices(deviceIds) {
      // each device contains groups and units arrays indicating where it belongs to
      const deviceRefs = this.devices.filter(d => deviceIds.includes(d.id))
      const devices = deviceRefs.map(d => {
        return { id: d.id, name: d.name, icon: 'mdi-camera' }
      })

      const units = this.units
        .filter(u => uniq(flatten(deviceRefs.map(d => d.units))).includes(u.id))
        .map(u => {
          return { id: u.id, name: u.name, icon: 'mdi-orbit' }
        })

      const groups = this.groups
        .filter(g =>
          uniq(flatten(deviceRefs.map(d => d.groups))).includes(g.id)
        )
        .map(g => {
          return { id: g.id, name: g.name, icon: 'mdi-folder' }
        })

      return concat(groups, units, devices)
    },
    getTimeArea() {
      return this.event.nonstop
        ? '24h'
        : `${this.event.startTime} ~ ${this.event.endTime}`
    },
    editEvent() {
      this.eventDraft = {
        playlist: this.event.playlist,
        devices: this.event.devices,
        startDate: this.event.startDate,
        endDate: this.event.endDate,
        startTime: this.event.startTime,
        endTime: this.event.endTime,
        pattern: this.event.pattern,
        color: this.event.color,
        nonstop: this.event.nonstop
      }

      this.editDialogOpen = true
      // Note: Used to close ScheduleEventDraft after opening ScheduleEventCard.
      // But doesn't open ScheduleEventCard if emit cancel.
      // Then Uncomment for now and observe
      // this.$emit('cancel')
    },
    saveEvent(event) {
      event.id = this.event.id
      this.editDialogOpen = false
      this.$emit('save', event)
    }
  },
  created() {
    const playlists = this.bindPlaylistsRef()
    const devices = this.bindDevicesRef()
    const deviceGroup = this.bindDeviceGroupsRef()
    const deviceUnit = this.bindDeviceUnitsRef()

    Promise.all([playlists, devices, deviceGroup, deviceUnit]).then(() => {
      this.eventLoading = false
    })
  }
}
</script>

<style lang="sass">
.event-disabled-treeselect
  .vue-treeselect__control
    border-radius: 0
    border: 0

  .vue-treeselect__control-arrow-container
    opacity: 0

.event-dialog
  overflow-y: visible !important
</style>
